import { useParams, Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { ArrowRight, CheckCircle2 } from 'lucide-react'
import SEO from '../components/seo/SEO'
import Button from '../components/ui/Button'
import { SERVICES } from '../data/services'
import { useLocalePath } from '../hooks/useLocalePath'
import styles from './ServiceDetail.module.css'

const PROCESS_STEPS = ['Scope', 'Design', 'Monitor', 'Verify']

export default function ServiceDetail() {
  const { slug } = useParams<{ slug: string }>()
  const { t } = useTranslation('services')
  const { t: tSeo } = useTranslation('seo')
  const lp = useLocalePath()

  const service = SERVICES.find(s => s.slug === slug)
  if (!service) return <Navigate to="/en/services" replace />

  const Icon = service.icon
  return (
    <div className={styles.page}>
      <SEO
        title={`${t(`items.${service.slug}.title`)} | Carbon13`}
        description={t(`items.${service.slug}.description`)}
        canonical={`/services/${service.slug}`}
      />

      <section className={styles.hero}>
        <div className={styles.container}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className={styles.iconWrap}>
              <Icon size={32} />
            </div>
            <h1>{t(`items.${service.slug}.title`)}</h1>
            <p className={styles.lead}>{t(`items.${service.slug}.description`)}</p>
          </motion.div>
        </div>
      </section>

      <section className={styles.capabilities}>
        <div className={styles.container}>
          <h2>{tSeo('serviceDetail.capabilities')}</h2>
          <div className={styles.capGrid}>
            {service.capabilities.map((cap, i) => (
              <motion.div
                key={i}
                className={styles.capItem}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
              >
                <CheckCircle2 size={18} />
                <span>{cap}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.process}>
        <div className={styles.container}>
          <h2>{tSeo('serviceDetail.process')}</h2>
          <div className={styles.processGrid}>
            {PROCESS_STEPS.map((step, i) => (
              <motion.div
                key={step}
                className={styles.processStep}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <span className={styles.stepNumber}>{String(i + 1).padStart(2, '0')}</span>
                <h3>{step}</h3>
                <p>{service.process[i]}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.deliverables}>
        <div className={styles.container}>
          <h2>{tSeo('serviceDetail.deliverables')}</h2>
          <div className={styles.deliverableGrid}>
            {service.deliverables.map((d, i) => (
              <div key={i} className={styles.deliverableItem}>
                <CheckCircle2 size={16} />
                <span>{d}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {service.faqs.length > 0 && (
        <section className={styles.faqs}>
          <div className={styles.container}>
            <h2>{tSeo('serviceDetail.faqs')}</h2>
            <div className={styles.faqList}>
              {service.faqs.map((faq, i) => (
                <div key={i} className={styles.faqItem}>
                  <h4>{faq.question}</h4>
                  <p>{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className={styles.cta}>
        <div className={styles.container}>
          <h2>Ready to get started?</h2>
          <p>Contact us to discuss your {t(`items.${service.slug}.title`).toLowerCase()} requirements.</p>
          <Button variant="secondary" size="lg" href={lp('/contact')}>
            Get in Touch <ArrowRight size={18} />
          </Button>
        </div>
      </section>
    </div>
  )
}
